import { FC, useCallback, useContext } from "react";
import { css, StyleSheet } from "aphrodite";
import PersonAvatar from "./PersonAvatar";
import { Route } from "nextjs-routes";
import {
  PathBuilderContext,
  PathBuilderContextState,
} from "@src/components/libraryItemDetailPages/module/contexts/PathBuilderContext";
import { getPromptScreenId } from "@src/components/libraryItemDetailPages/course/hooks/useCourseScreensAtom";
import { ActiveClientFragment } from "@src/components/libraryItemDetailPages/course/build/CourseBuildContent.generated";
import { useAtomCallback } from "jotai/utils";
import { pathVersionAtom } from "@src/components/libraryItemDetailPages/module/atoms";
import { courseVersionAtom } from "@src/components/libraryItemDetailPages/course/atoms";

type Props = {
  activeClients: ActiveClientFragment[];
  size: "24px" | "32px" | "40px";
};

const ActiveClientsAvatarStack: FC<Props> = ({ activeClients, size }) => {
  const pathBuilderContext = useContext(PathBuilderContext);
  const getRouteFromActiveClients = useAtomCallback(
    useCallback(
      (
        get,
        set,
        pathBuilderContext: PathBuilderContextState | undefined,
        activeClient: ActiveClientFragment,
        libraryItemDetailPageEnabled?: boolean,
      ): Route | undefined => {
        const pathVersion = get(pathVersionAtom);
        const courseVersion = get(courseVersionAtom);
        if (pathBuilderContext) {
          if (
            activeClient.pathVersionId &&
            activeClient.pathContentMembershipId
          ) {
            if (libraryItemDetailPageEnabled && pathVersion?.path.libraryItem) {
              return {
                pathname: "/library/library-item/[id]",
                query: {
                  id: pathVersion?.path.libraryItem.id,
                  m: activeClient.pathContentMembershipId,
                  screenId: activeClient.promptUuid
                    ? getPromptScreenId(activeClient.promptUuid)
                    : undefined,
                },
              };
            } else {
              return {
                pathname: "/builder/path-version/[id]",
                query: {
                  id: activeClient.pathVersionId,
                  m: activeClient.pathContentMembershipId,
                  screenId: activeClient.promptUuid
                    ? getPromptScreenId(activeClient.promptUuid)
                    : undefined,
                },
              };
            }
          }
        } else if (courseVersion) {
          if (activeClient.courseVersionId) {
            if (
              libraryItemDetailPageEnabled &&
              courseVersion?.course.libraryItem
            ) {
              return {
                pathname: "/library/library-item/[id]",
                query: {
                  id: courseVersion?.course.libraryItem.id,
                  screenId: activeClient.promptUuid
                    ? getPromptScreenId(activeClient.promptUuid)
                    : undefined,
                },
              };
            } else {
              return {
                pathname: "/builder/course-version/[id]",
                query: {
                  id: activeClient.courseVersionId.toString(),
                  screenId: activeClient.promptUuid
                    ? getPromptScreenId(activeClient.promptUuid)
                    : undefined,
                },
              };
            }
          }
        }
      },
      [],
    ),
  );
  return (
    <span className={css(styles.container)}>
      {activeClients.map((activeClient, i) => {
        return (
          <PersonAvatar
            person={activeClient.user}
            size={size}
            key={activeClient.clientId}
            styleDeclaration={i > 0 ? styles.shift : undefined}
            route={getRouteFromActiveClients(pathBuilderContext, activeClient)}
          />
        );
      })}
    </span>
  );
};

const styles = StyleSheet.create({
  container: {
    display: "flex",
  },
  shift: {
    marginLeft: -10,
  },
});

export default ActiveClientsAvatarStack;
